import React, { useState } from 'react'
import groupBy from 'lodash/groupBy'
import {
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Button,
  Box,
  NestedList,
  LockIcon,
} from 'components'
import { differenceInCalendarDays } from 'date-fns'

const SelectMarketList = ({
  onChange,
  isValid,
  onSubmit,
  userMarkets,
  submitLabel = 'Submit',
  initialState = [],
  border = true,
  markets = {},
}) => {
  const initialMarkets = Object.values(markets).filter((m) =>
    initialState.includes(m.id),
  )

  const [active, setActive] = useState(initialMarkets ? initialMarkets : [])

  if (Object.values(markets).length === 0) {
    return null
  }

  const DecoratedItemComponent = (item) => {
    const isActive = !!active.find((a) => a.id === item.id)
    const onClick = () => {
      const newActive = isActive
        ? active.filter((a) => a.id !== item.id)
        : active.concat([item])
      setActive(newActive)
      onChange && onChange(newActive)
    }

    return (
      <SelectMarketListItem {...item} isActive={isActive} onClick={onClick} />
    )
  }

  return (
    <>
      <MarketNestedList
        border={border}
        readOnly={false}
        markets={Object.values(markets)
          .map((m) => ({
            ...m,
            isSubscription: true,
          }))
          .sort((a, b) =>
            a.disabled && !b.disabled ? 1 : !a.disabled && b.disabled ? -1 : 0,
          )}
        ItemComponent={DecoratedItemComponent}
        userMarkets={userMarkets}
      />

      {onSubmit && (
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isValid ? !isValid(active) : active.length === 0}
          onClick={() => onSubmit(active)}
        >
          {submitLabel}
        </Button>
      )}
    </>
  )
}

export const MarketNestedList = ({
  markets,
  ItemComponent = SelectMarketListItem,
  userMarkets = [],
  border = true,
  readOnly = true,
}) => {
  const items = Object.values(groupBy(markets, 'industry')).map((markets) => ({
    label: markets[0].industry,
    industryId: markets[0].industryId,
    children: markets
      .sort(({ index: a = 0 }, { index: b = 0 }) => a - b)
      .map((market) => ({
        label: market.cityName,
        id: market.id,
        secondary: getSecondaryStringForMarket(market),
        cancelledAt: market.cancelledAt,
        locked: userMarkets.includes(market.id),
        disabled: market.disabled,
      })),
  }))

  return (
    <Box style={{ pointerEvents: readOnly ? 'none' : 'all' }}>
      <Box
        border={border ? '1px solid #999' : null}
        py={border ? 2 : 0}
        my={border ? 4 : 2}
        className={border ? 'hard-shadow' : null}
      >
        <NestedList
          initialState={{ 'Advertising/Marketing': true }}
          ItemComponent={ItemComponent}
          items={items}
        />
      </Box>
    </Box>
  )
}

export default SelectMarketList

const SelectMarketListItem = ({
  isActive = true,
  onClick,
  locked,
  disabled,
  cancelledAt,
  hidden,
  nested,
  margin,
  label,
  secondary,
}) => {
  return (
    <ListItem disabled={disabled || hidden || locked} button onClick={onClick}>
      {locked && <LockIcon />}
      <ListItemText
        primary={label}
        secondary={secondary}
        primaryTypographyProps={{
          style: {
            marginLeft: nested ? margin : 0,
            fontSize: 24,
            opacity: cancelledAt ? 0.5 : 1,
          },
        }}
        secondaryTypographyProps={{
          style: {
            marginLeft: nested ? margin : 0,
          },
        }}
      />
      <ListItemSecondaryAction>
        <Checkbox
          disabled={disabled || hidden || locked}
          edge="end"
          checked={!cancelledAt && isActive && !disabled}
          onChange={onClick}
          color={isActive ? '#49b592' : null}
          style={{ marginRight: 0 }}
        />
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const getSecondaryStringForMarket = (market) => {
  if (market.isReactivated) {
    return 'Reactivated'
  }

  if (market.cancelledAt) {
    if (typeof market.cancelledAt === 'boolean') {
      return 'Cancelled'
    }

    return `Cancelled, expires in ${differenceInCalendarDays(
      new Date(market.activeUntil.toDate()),
      new Date(),
    )} days`
  }

  if (market.isAdded) {
    return 'Added'
  }

  if (market.isActive) {
    return 'Already active'
  }
  return ''
}
